import React from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import LoginButton from "../components/LoginButton";
import StockSearch from "../components/StockSearch";
import { useColorModeValue } from "../components/ui/color-mode";
import { SIGNATURE_GREEN } from "../theme/brandColors";
import HomeFeatureSection from "./HomeFeatureSection";
import HomeHowItWorksSection from "./HomeHowItWorksSection";
import HomePublicMetricsSection from "./HomePublicMetricsSection";

const exampleStocks = [
  { ticker: "AAPL", name: "Apple", href: "/stock/aapl" },
  { ticker: "MSFT", name: "Microsoft", href: "/stock/msft" },
  { ticker: "NVDA", name: "NVIDIA", href: "/stock/nvda" },
  { ticker: "BRK-B", name: "Berkshire Hathaway", href: "/stock/brk-b" },
];

const HomeMarketingContent = ({ onSuggestionSelected }) => {
  const bandBg = useColorModeValue("white", "gray.900");
  const subtleBg = useColorModeValue("gray.100", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("teal.700", "teal.300");
  const linkHoverBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const heroLinkProps = {
    color: linkColor,
    fontWeight: "semibold",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    textUnderlineOffset: "4px",
    transition: "background-color 0.2s ease, color 0.2s ease, text-decoration-color 0.2s ease",
    px: 1,
    borderRadius: "sm",
    _hover: {
      bg: linkHoverBg,
      color: SIGNATURE_GREEN,
      textDecoration: "underline",
      textDecorationStyle: "solid",
    },
  };

  return (
    <>
      <Box as="section" bg={bandBg} borderBottomWidth="1px" borderColor={borderColor}>
        <Grid
          maxW="1180px"
          mx="auto"
          px={{ base: 5, md: 8 }}
          py={{ base: 12, md: 16 }}
          gap={{ base: 10, lg: 14 }}
          templateColumns={{ base: "1fr", lg: "minmax(0, 1.1fr) minmax(340px, 0.9fr)" }}
          alignItems="center"
        >
          <VStack align="start" gap={5}>
            <Text
              w="100%"
              textAlign="center"
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              color={SIGNATURE_GREEN}
              style={{ color: SIGNATURE_GREEN }}
              textTransform="uppercase"
            >
              DCF Teacher
            </Text>
            <Heading as="h1" size={{ base: "3xl", md: "4xl" }} lineHeight="1.05">
              Learn stock valuation with discounted cash flow tools.
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={mutedTextColor} lineHeight="1.7">
              DCF Teacher combines a DCF calculator, reverse DCF analysis, historical financials,
              SEC filings, charts, and filing-sourced public metrics so investors can connect a
              company&apos;s fundamentals to intrinsic value.
            </Text>
            <LoginButton
              size="lg"
              alignSelf="center"
              bg={SIGNATURE_GREEN}
              color="white"
              style={{ backgroundColor: SIGNATURE_GREEN }}
              transition="transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease"
              _hover={{
                bg: SIGNATURE_GREEN,
                boxShadow: "lg",
                filter: "brightness(0.95)",
                transform: "translateY(-2px)",
              }}
            >
              Create a Free Account
            </LoginButton>
            <Flex wrap="wrap" gap={3} pt={2}>
              <Link href="/browse" {...heroLinkProps}>
                Browse stocks
              </Link>
              <Link href="/metrics" {...heroLinkProps}>
                Public KPI data
              </Link>
              <Link href="/tutorials" {...heroLinkProps}>
                Learn DCF
              </Link>
              <Link href="/stock-baskets" {...heroLinkProps}>
                Explore stock baskets
              </Link>
            </Flex>
          </VStack>

          <Box
            bg={subtleBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={{ base: 5, md: 6 }}
          >
            <VStack align="stretch" gap={4}>
              <Box>
                <Heading as="h2" size="md">
                  Search stocks
                </Heading>
                <Text mt={2} color={mutedTextColor}>
                  Search public stocks by ticker or company name, or find pre-IPO SEC filers by
                  legal name, CIK, or proposed ticker.
                </Text>
              </Box>
              <Box>
                <Text
                  as="label"
                  htmlFor="homepage-stock-search"
                  display="block"
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={2}
                >
                  Stock search
                </Text>
                <StockSearch
                  onSuggestionSelected={onSuggestionSelected}
                  inputProps={{
                    id: "homepage-stock-search",
                    "aria-label": "Search by ticker, company name, or CIK",
                  }}
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  Example stocks
                </Text>
                <Flex wrap="wrap" gap={2}>
                  {exampleStocks.map((stock) => (
                    <Link
                      key={stock.ticker}
                      href={stock.href}
                      px={3}
                      py={2}
                      bg={bandBg}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      {stock.ticker}{" "}
                      <Text as="span" color={mutedTextColor} fontWeight="normal">
                        {stock.name}
                      </Text>
                    </Link>
                  ))}
                </Flex>
              </Box>
            </VStack>
          </Box>
        </Grid>
      </Box>

      <HomeFeatureSection />
      <HomePublicMetricsSection />
      <HomeHowItWorksSection />
    </>
  );
};

export default HomeMarketingContent;
